import { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import MultiPurpose from "../aftersales_multipurpose/MultiPurpose";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import UserIcon from "../../../assets/aftersalesservice_icons/user.svg";
import ApiService from "../../../components/Api_services";
import {
  PHONE_COUNTRY_OPTIONS,
  inferCountryFromName,
  splitPhoneInput,
  isValidRegionalPhone,
  detectCountryFromFullNumber,
} from "../warrantyPhoneUtils";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const TicketSubmission = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);
  const rawType = searchParams.get("type") || "";
  const type =
    rawType === "General_Maintenance"
      ? "General Maintenance"
      : rawType === "Repair_Service"
        ? "Repair Service"
        : rawType === "Technical_Consultation_Inquiry"
          ? "Technical Consultation / Inquiry"
          : rawType;

  const [ticketInfo, setTicketInfo] = useState({
    custname: "",
    custtelno: "",
    emailadd: "",
    salesorder: "",
  });
  const [loading, setLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [errors, setErrors] = useState({
    custname: "",
    custtelno: "",
  });
  const [cleanPhoneNumber, setCleanPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("MY");
  const [ticketStatus, setTicketStatus] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchTicketStatus = async () => {
      const payload = {
        serial_no: serialNoForApi,
        serial_type: serialType,
        type: type,
      };

      try {
        const response = await ApiService.checkTicketStatus(payload);
        if (response && response.status === 200) {
          setTicketStatus(response.status);
        }
      } catch (error) {
        console.error("Error fetching ticket status:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchTicketStatus();
  }, [serialNoForApi, serialType, type]);

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);

        if (response && response.status === 200) {
          const customer = response.data?.customer_data || null;
          const phoneRaw = customer?.phone_no || "";
          const defaultCountry = inferCountryFromName(customer?.country);
          const normalized = splitPhoneInput(phoneRaw, defaultCountry);
          setPhoneCountry(normalized.countryId || defaultCountry);
          setCleanPhoneNumber(normalized.digit_only);

          const updateFields = {
            custname: customer?.receiver_name,
            custtelno: normalized.nationalDisplay || normalized.national || "",
            salesorder: customer?.sales_order,
          };

          setTicketInfo((prev) => ({ ...prev, ...updateFields }));
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  const handlePhoneInput = (value) => {
    const split = splitPhoneInput(value, phoneCountry);
    const digits = (value || "").replace(/[^\d]/g, "");
    if (detectCountryFromFullNumber(digits)) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setTicketInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || "",
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handlePhoneBlur = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return;

    const split = splitPhoneInput(trimmed, phoneCountry);
    if (detectCountryFromFullNumber(trimmed.replace(/[^\d]/g, ""))) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setTicketInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || trimmed,
    }));
  };

  const handlePhoneCountryChange = (countryId) => {
    setPhoneCountry(countryId);
    if (!ticketInfo.custtelno?.trim()) return;

    const split = splitPhoneInput(ticketInfo.custtelno, countryId);
    setCleanPhoneNumber(split.digit_only);
    setTicketInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || prev.custtelno,
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handleSubmitTicket = async () => {
    if (loading) return;
    setLoading(true);
    setNotifMessage("");

    let newErrors = { custname: "", custtelno: "" };
    let hasError = false;

    if (!ticketInfo.custname) {
      newErrors.custname = "Please enter buyer name";
      hasError = true;
    }

    if (!ticketInfo.custtelno) {
      newErrors.custtelno = "Please enter mobile number";
      hasError = true;
    } else if (!isValidRegionalPhone(ticketInfo.custtelno, phoneCountry)) {
      newErrors.custtelno = "Invalid mobile number format for selected country";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      setLoading(false);
      return;
    }

    const submitPhone =
      cleanPhoneNumber ||
      splitPhoneInput(ticketInfo.custtelno, phoneCountry).digit_only;

    const payload = {
      serial_no: serialNoForApi,
      serial_type: serialType,
      name: ticketInfo.custname,
      phone: submitPhone,
      phone_country: phoneCountry,
      email: ticketInfo.emailadd,
      type: type,
      sales_order: ticketInfo.salesorder,
    };

    try {
      const response = await ApiService.createTicket(payload);
      if (response && response.status === 200) {
        setNotifMessage("Ticket created successfully!");
        setShowMessage(true);
      } else {
        console.error("Error creating ticket:", response);
        setNotifMessage(response?.message || "Failed to create ticket.");
        setShowMessage(true);
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      setNotifMessage("Failed to create ticket.");
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const goBack = (e) => {
    e.preventDefault();
    window.history.back();
  };

  if (pageLoading) {
    return (
      <AfterSalesLayout>
        <div style={{ textAlign: "center", margin: "40px 0" }}>Loading...</div>
      </AfterSalesLayout>
    );
  }

  return (
    <AfterSalesLayout>
      {ticketStatus ? (
        <Navigate
          to={`/aftersalesservice/ticketsubmitted${buildAfterSalesQuery(
            searchParams,
            {
              type: rawType,
            },
          )}`}
          replace
        />
      ) : (
        <>
          <div
            className="as-ticket-submission-content"
            id="ticket_submission_modal"
          >
            <div
              className="as-ticket-submission-page"
              id="ticket-submission-show"
            >
              <div style={{ padding: "0 31.97px" }}>
                <div className="as-header-card" style={{ textAlign: "left" }}>
                  <button
                    className="as-back-btn"
                    id="ticket_submission_back"
                    style={{ marginBottom: "10px" }}
                    aria-label="Go back"
                    onClick={goBack}
                  >
                    <FallbackImg
                      src={BackBlue}
                      alt="backblue"
                      width="20"
                      height="20"
                    />
                    <span>{trans.back}</span>
                  </button>

                  <h2 className="as-ticket-submission-font-2">
                    {trans.ticket_submit}
                  </h2>
                  <p className="as-ticket-submission-font-1">
                    {trans.ticket_submit_sub}
                  </p>
                </div>

                <div className="as-ticket-submission-card">
                  <div className="as-header-row">
                    <FallbackImg src={UserIcon} alt="usericon" />
                    <div>
                      <div className="as-ticket-submission-font-1">
                        {trans.contact_details}
                      </div>
                      <div className="as-subtitle">
                        {trans.contact_details_sub_2}
                      </div>
                    </div>
                  </div>

                  <div id="req_form_detail">
                    <label className="as-ticket-submission-font-1">
                      {trans.contact_name}
                    </label>
                    <input
                      type="text"
                      className="as-ticket-submission-font-1"
                      id="reqreg_custname"
                      name="reqreg_custname"
                      value={ticketInfo.custname}
                      onChange={(e) =>
                        setTicketInfo((p) => ({
                          ...p,
                          custname: e.target.value,
                        }))
                      }
                      maxLength={100}
                    />
                    {errors.custname && (
                      <div className="as-error">{errors.custname}</div>
                    )}

                    <label className="as-ticket-submission-font-1">
                      {trans.mobile_no}
                    </label>
                    <div className="as-csn_phone-row as-ticket-submission-phone-row">
                      <select
                        className="as-ticket-submission-font-1 as-ticket-submission-phone-country-select"
                        value={phoneCountry}
                        onChange={(e) =>
                          handlePhoneCountryChange(e.target.value)
                        }
                        aria-label="Country code"
                      >
                        {PHONE_COUNTRY_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.dial}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="as-ticket-submission-font-1 as-ticket-submission-phone-number-input"
                        id="reqreg_custtelno"
                        name="reqreg_custtelno"
                        value={ticketInfo.custtelno}
                        onChange={(e) => handlePhoneInput(e.target.value)}
                        onBlur={(e) => handlePhoneBlur(e.target.value)}
                        maxLength={100}
                        placeholder="Phone number"
                      />
                    </div>
                    {errors.custtelno && (
                      <div className="as-error">{errors.custtelno}</div>
                    )}

                    <label className="as-ticket-submission-font-1">
                      {trans.email_add}
                    </label>
                    <input
                      type="text"
                      className="as-ticket-submission-font-1"
                      id="reqreg_email"
                      name="reqreg_email"
                      value={ticketInfo.emailadd}
                      onChange={(e) =>
                        setTicketInfo((p) => ({
                          ...p,
                          emailadd: e.target.value,
                        }))
                      }
                      maxLength={100}
                      placeholder="Optional"
                    />

                    <button
                      type="button"
                      className="as-submit-btn as-ticket-submission-font-1"
                      id="req_submit_btn"
                      onClick={handleSubmitTicket}
                    >
                      {trans.submit}
                    </button>
                  </div>
                </div>

                <div className="as-card-1">
                  <span className="as-ticket-submission-font-1">
                    {trans.our_sup_4}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {showMessage && (
            <MultiPurpose
              title="Ticket Creation Status"
              message={notifMessage}
              yesText="OK"
              hideNo={true}
              onYes={() => {
                setShowMessage(false);
                window.location.reload();
              }}
              onClose={() => {
                setShowMessage(false);
                window.location.reload();
              }}
            />
          )}
        </>
      )}
    </AfterSalesLayout>
  );
};

export default TicketSubmission;
